import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { useSEO } from "@/hooks/useSEO";

export default function Login() {
  const { login } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  useSEO({
    title: lang === "es"
      ? "Iniciar sesión — ChefSync P&L | Panel financiero para restaurantes"
      : "Log in — ChefSync P&L | Restaurant financial dashboard",
    description: lang === "es"
      ? "Accede a tu panel ChefSync P&L: gestiona facturas de proveedores, ingresos, salarios y márgenes de escandallo en tiempo real con OCR / IA."
      : "Log in to ChefSync P&L: manage vendor invoices, income, payroll and dish-costing margins in real time with AI OCR.",
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("welcome"));
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: form */}
      <div className="w-full lg:w-[520px] flex flex-col p-8 lg:p-12 bg-[var(--surface)]">
        <div className="flex items-center justify-between mb-16">
          <Link to="/" className="flex items-center gap-2" data-testid="login-logo-home">
            <Logo size={32} />
            <span className="font-display font-bold text-base">
              ChefSync <span className="text-[var(--text-muted)] font-normal">P&amp;L</span>
            </span>
          </Link>
          <button
            onClick={() => setLang(lang === "es" ? "en" : "es")}
            data-testid="lang-toggle-auth"
            className="text-xs uppercase tracking-wider text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            {lang === "es" ? "EN" : "ES"}
          </button>
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-sm">
          <h1 className="font-display text-4xl font-bold tracking-tight leading-none mb-2">
            {t("welcome")}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mb-10">
            {lang === "es" ? "Control financiero, sin esfuerzo." : "Financial control, effortless."}
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
                {t("email")}
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="h-11"
                placeholder="you@email.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
                {t("password")}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="h-11 pr-11"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                  aria-label={showPwd ? (lang === "es" ? "Ocultar contraseña" : "Hide password") : (lang === "es" ? "Mostrar contraseña" : "Show password")}
                  data-testid="login-toggle-pwd"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                data-testid="link-forgot-password"
                className="text-xs text-[var(--primary)] hover:underline"
              >
                {t("forgotPassword")}
              </Link>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-11 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
            >
              {loading ? "..." : t("login")}
            </Button>
          </form>

          <p className="mt-8 text-sm text-[var(--text-secondary)]">
            {t("dontHaveAccount")}{" "}
            <Link to="/register" data-testid="link-register" className="text-[var(--primary)] font-medium hover:underline">
              {t("createAccount")}
            </Link>
          </p>
        </div>
      </div>

      {/* Right: marketing hero */}
      <div
        className="hidden lg:flex flex-1 relative items-center p-16"
        style={{
          background: "linear-gradient(135deg, #2C4C3B 0%, #1a3025 100%)",
        }}
        data-testid="login-hero-panel"
      >
        <div className="text-white max-w-lg">
          <p className="text-xs uppercase tracking-[0.2em] opacity-70 mb-6">
            {lang === "es" ? "Software financiero para hostelería" : "Financial software for restaurants"}
          </p>
          <h2 className="font-display text-4xl font-bold leading-tight mb-6">
            {lang === "es"
              ? "Facturas, food cost, labour cost y escandallos — en tiempo real."
              : "Invoices, food cost, labour cost and dish costing — in real time."}
          </h2>
          <p className="text-base opacity-90 leading-relaxed">
            {lang === "es"
              ? "ChefSync P&L combina OCR con IA, contabilidad de proveedores y análisis de márgenes por plato. Sube tus gastos e ingresos y todo se calcula automáticamente."
              : "ChefSync P&L combines AI-powered OCR, vendor accounting and per-dish margin analytics. Upload your expenses and income and everything is computed automatically."}
          </p>
        </div>
      </div>
    </div>
  );
}
